import React from 'react';
import { ChevronUp, ChevronDown, Square } from 'lucide-react';

export interface OpenCloseButtonsProps {
  value: number;                     // 0-100
  onOpen: () => void;                // Command to open
  onClose: () => void;               // Command to close
  onStop?: () => void;               // Command to stop (optional)
  disabled?: boolean;
  showValue?: boolean;
  colorScheme?: 'teal' | 'cyan' | 'blue';
}

/**
 * OpenClose Button Control
 * 
 * 開/關/停 按鈕控制
 * - 適用於不支援精確百分比的設備
 * - 或用戶偏好簡單操作
 * 
 * MCP Commands:
 * - Open: OpenClose { openPercent: 100 }
 * - Close: OpenClose { openPercent: 0 }
 * - Stop: (device-specific, may not be available)
 */
export const Style02_Buttons: React.FC<OpenCloseButtonsProps> = ({
  value,
  onOpen,
  onClose,
  onStop,
  disabled = false,
  showValue = true,
  colorScheme = 'teal',
}) => {
  // Color scheme mapping
  const colorMap = {
    teal: {
      active: 'bg-gradient-to-r from-teal-600 to-teal-400 text-white',
      text: 'text-teal-400',
      border: 'border-teal-500',
    },
    cyan: {
      active: 'bg-gradient-to-r from-cyan-600 to-cyan-400 text-white',
      text: 'text-cyan-400',
      border: 'border-cyan-500',
    },
    blue: {
      active: 'bg-gradient-to-r from-blue-600 to-blue-400 text-white',
      text: 'text-blue-400',
      border: 'border-blue-500',
    },
  };

  const colors = colorMap[colorScheme];
  const isOpen = value > 50;
  const isClosed = value === 0;
  const isPartiallyOpen = value > 0 && value < 100;

  return (
    <div className="space-y-4">
      {/* Current status */}
      {showValue && (
        <div className="text-center">
          <span className={`text-3xl font-bold ${colors.text}`}>
            {value}%
          </span>
          <p className="text-sm text-slate-400 mt-1">
            {isClosed ? '已關閉' : isPartiallyOpen ? '半開' : '已打開'}
          </p>
        </div>
      )}

      {/* Control buttons */}
      <div className="flex gap-3 justify-center">
        {/* Open button */}
        <button
          onClick={onOpen}
          disabled={disabled || value === 100}
          className={`flex flex-col items-center justify-center w-20 h-20 rounded-2xl transition-all
            ${value === 100 
              ? colors.active 
              : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700 border border-slate-600'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <ChevronUp className="w-8 h-8" />
          <span className="text-xs mt-1">打開</span>
        </button>

        {/* Stop button (if available) */}
        {onStop && (
          <button
            onClick={onStop}
            disabled={disabled}
            className={`flex flex-col items-center justify-center w-20 h-20 rounded-2xl transition-all
              bg-slate-800/50 text-slate-300 hover:bg-slate-700 border border-slate-600
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <Square className="w-6 h-6" />
            <span className="text-xs mt-1">停止</span>
          </button>
        )}

        {/* Close button */}
        <button
          onClick={onClose}
          disabled={disabled || value === 0}
          className={`flex flex-col items-center justify-center w-20 h-20 rounded-2xl transition-all
            ${value === 0 
              ? 'bg-slate-600 text-white' 
              : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700 border border-slate-600'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <ChevronDown className="w-8 h-8" />
          <span className="text-xs mt-1">關閉</span>
        </button>
      </div>
    </div>
  );
};

export default Style02_Buttons;
